import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { CacheService } from '../cache/cache.service';

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  constructor(private cacheService: CacheService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if this is a save operation that should invalidate cache
    if (req.url.includes('/saveReportingDates')) {
      // Clear cache for getCommunityReportingDates
      this.clearRelatedCache(req);
      // Continue with the save request
      return next.handle(req);
    }

    // Only cache GET and POST requests
    if (!['GET', 'POST'].includes(req.method)) {
      return next.handle(req);
    }

    // Check if this endpoint should be cached
    const config = this.cacheService.getCacheConfig(req.url);
    if (!config) {
      return next.handle(req);
    }

    // Generate cache key
    const cacheKey = this.cacheService.generateCacheKey(req, config);

    // Check for cached response
    const cachedResponse = this.cacheService.get(cacheKey);

    if (cachedResponse) {
      // Log cache hit in development mode
      if (!environment.production) {
        // console.log(`[Cache Hit] ${req.method} ${req.url}`);
      }

      this.cacheService.recordHit(config.endpoint);

      // Return cached response as observable
      return of(cachedResponse);
    }

    // Log cache miss in development mode
    if (!environment.production) {
      // console.log(`[Cache Miss] ${req.method} ${req.url}`);
    }

    this.cacheService.recordMiss(config.endpoint);

    // Make the request and cache successful responses
    const startTime = Date.now();

    return next.handle(req).pipe(
      tap(event => {
        // Only cache successful HTTP responses
        if (event instanceof HttpResponse) {
          const responseTime = Date.now() - startTime;

          // Check if response should be cached based on custom logic
          const shouldCache = config.shouldCache ?
            config.shouldCache(event.body) :
            true;

          // Only cache successful responses (2xx status codes)
          if (shouldCache && event.status >= 200 && event.status < 300) {
            // Check if response has the expected structure
            if (event.body && typeof event.body === 'object') {
              // For YiaYia API responses, check status field
              const apiResponse = event.body as any;
              if (!apiResponse.hasOwnProperty('status') || apiResponse.status === 1) {
                this.cacheService.set(cacheKey, event, config.ttl, config.endpoint);

                if (!environment.production) {
                  // console.log(`[Cache Set] ${req.method} ${req.url} (${responseTime}ms)`);
                }
              } else if (!environment.production) {
                // console.log(`[Cache Skip] ${req.method} ${req.url} - API returned error status`);
              }
            } else {
              // For non-standard responses, cache if shouldCache allows it
              this.cacheService.set(cacheKey, event, config.ttl, config.endpoint);
            }
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // Don't cache error responses
        if (!environment.production) {
          console.error(`[Cache Error] ${req.method} ${req.url}`, error);
        }
        return throwError(() => error);
      })
    );
  }

  private clearRelatedCache(req: HttpRequest<any>): void {
    // Extract communityId from request
    const body = req.body as any;
    const params = req.params;
    const communityId = body?.communityId || params.get('communityId') || 'default';

    // Clear cache for getCommunityReportingDates with this communityId
    const cacheKey = `reporting_dates_${communityId}`;
    this.cacheService.delete(cacheKey);

    if (!environment.production) {
      // console.log(`[Cache Clear] Cleared cache for getCommunityReportingDates (communityId: ${communityId})`);
    }
  }
}

// Import environment to check if we're in production
import { environment } from '../../../environments/environment';
